/**
 * Markdown LÉGER de Reps — la logique PURE, sans aucun JSX.
 *
 * Source de vérité UNIQUE du parsing : le rendu web (RepsExercise.jsx) importe
 * ces fonctions, et le serveur de voix (lib/bebotSpeechText.js) les réutilise
 * pour énumérer les mots affichés dans LE MÊME ORDRE que le rendu. C'est ce qui
 * permet au karaoké (BeBot lit, les mots s'allument) d'aligner ses timings sur
 * les mots réellement à l'écran sans dupliquer une seule règle de parsing.
 *
 * Le port mobile (reps-app/src/features/lesson/markdown.tsx) reste une copie
 * TypeScript volontairement identique : toute évolution ici doit y être
 * répercutée, le compte de mots doit tomber juste des deux côtés (sinon le
 * karaoké se coupe de lui-même côté app, par la vérification de compte).
 */

// Mots-clés/fonctions à styler en code même sans backticks. Liste volontairement
// restreinte à des tokens SANS ambiguïté avec le français (pas de « range » = il
// range, « type », « or », « in », « for »...). Les frontières de mot évitent les
// faux positifs (ex. « print » dans « printemps » n'est pas capturé).
export const CODE_TOKENS =
    "print|input|len|def|elif|else|return|import|None|True|False|append|str|int|float|bool";
// Frontière de mot Unicode-aware. En JS, \b et \w ignorent les lettres accentuées :
// la limite entre « int » et « é » est vue comme une frontière de mot, donc \bint\b
// capturerait « int » dans « intérêt » (ou « réintégrer »). On remplace \b par des
// assertions qui traitent toute lettre/chiffre Unicode comme un caractère de mot
// (flag "u" requis pour \p{L}).
const NOT_WORD_BEFORE = "(?<![\\p{L}\\p{N}_])";
const NOT_WORD_AFTER = "(?![\\p{L}\\p{N}_])";
// Ordre des alternatives : backticks, balises HTML, appel fonction (word() ou
// word(2+ caractères, pour éviter les pluriels « (s) »), mots-clés, hN.
export const RICH_RE = new RegExp(
    "(`[^`]+`|</?[a-zA-Z][^>]*>|" +
        NOT_WORD_BEFORE +
        "[a-zA-Z_]\\w*\\(\\)|" +
        NOT_WORD_BEFORE +
        "[a-zA-Z_]\\w*\\([^)]{2,}\\)|" +
        NOT_WORD_BEFORE +
        "(?:" +
        CODE_TOKENS +
        ")" +
        NOT_WORD_AFTER +
        "|" +
        NOT_WORD_BEFORE +
        "h[1-6]" +
        NOT_WORD_AFTER +
        ")",
    "gu"
);

// Règle de flanquement façon CommonMark : PAS d'espace collé aux marqueurs
// (ni après l'ouvrant, ni avant le fermant). Indispensable pour le contenu
// existant écrit en texte brut : « "*" * 20 », « a *= 2 et b *= 3 » ou
// « 2 ** 3 ** 4 » sans backticks ne déclenchent jamais d'emphase.
// Gras strict : **...** sans astérisque ni retour à la ligne à l'intérieur.
const MD_BOLD_SRC = "\\*\\*[^*\\s](?:[^*\\n]*[^*\\s])?\\*\\*";
// Italique strict, appliqué APRÈS le gras.
const MD_ITALIC_SRC = "\\*[^*\\s](?:[^*\\n]*[^*\\s])?\\*";
// Le split() et le test ancré DOIVENT partager la même source, sinon un
// candidat capturé par l'un serait rejeté par l'autre.
const MD_BOLD_RE = new RegExp("(" + MD_BOLD_SRC + ")", "g");
const MD_ITALIC_RE = new RegExp("(" + MD_ITALIC_SRC + ")", "g");
const MD_BOLD_ONLY_RE = new RegExp("^" + MD_BOLD_SRC + "$");
const MD_ITALIC_ONLY_RE = new RegExp("^" + MD_ITALIC_SRC + "$");

// Lien [libellé](url) : URL acceptée UNIQUEMENT en http:// ou https:// (jamais
// javascript: ni data: — le texte reste brut sinon). L'alternative backtick,
// testée en position, CONSOMME les spans `...` : un lien écrit à l'intérieur
// d'un backtick reste du code, jamais un lien. Libellé sans "]" ni retour à
// la ligne ; URL sans espace ni parenthèse fermante.
const MD_LINK_SPLIT_RE = /(`[^`]+`)|\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)/g;

// Découpe un segment en runs { type:"text", text } | { type:"link", label, url }.
// Les spans backtick restent DANS les runs texte (stylés plus bas par le
// pipeline inline habituel). Un texte SANS lien ressort en UN seul run texte
// STRICTEMENT identique à l'entrée (non-régression garantie).
export function splitLinks(text) {
    const src = String(text == null ? "" : text);
    const out = [];
    let last = 0;
    MD_LINK_SPLIT_RE.lastIndex = 0;
    let m;
    while ((m = MD_LINK_SPLIT_RE.exec(src)) !== null) {
        if (m[1]) continue; // span backtick : opaque, reste du texte brut
        if (m.index > last) {
            out.push({ type: "text", text: src.slice(last, m.index) });
        }
        out.push({ type: "link", label: m[2], url: m[3] });
        last = m.index + m[0].length;
    }
    if (last < src.length) out.push({ type: "text", text: src.slice(last) });
    if (!out.length) out.push({ type: "text", text: "" });
    return out;
}

// Découpe un segment texte (hors code) en runs { type: "text"|"strong"|"em" }.
export function splitEmphasis(text) {
    const out = [];
    const boldParts = String(text == null ? "" : text).split(MD_BOLD_RE);
    for (const part of boldParts) {
        if (!part) continue;
        if (MD_BOLD_ONLY_RE.test(part)) {
            out.push({ type: "strong", text: part.slice(2, -2) });
            continue;
        }
        for (const ip of part.split(MD_ITALIC_RE)) {
            if (!ip) continue;
            if (MD_ITALIC_ONLY_RE.test(ip)) {
                out.push({ type: "em", text: ip.slice(1, -1) });
            } else {
                out.push({ type: "text", text: ip });
            }
        }
    }
    if (!out.length) out.push({ type: "text", text: "" });
    return out;
}

// Découpe un body en blocs : { type:"p", lines:[...] } | { type:"ul", items:[...] }.
// Ligne vide = séparation de paragraphes ; lignes consécutives "- " = liste à
// puces ; le \n simple reste DANS le paragraphe (rendu <br> plus bas).
export function splitBlocks(text) {
    const blocks = [];
    let para = [];
    let items = [];
    function flushPara() {
        if (para.length) blocks.push({ type: "p", lines: para });
        para = [];
    }
    function flushList() {
        if (items.length) blocks.push({ type: "ul", items });
        items = [];
    }
    const chunks = String(text == null ? "" : text).split(/\n[ \t]*\n+/);
    for (const chunk of chunks) {
        for (const line of chunk.split("\n")) {
            const m = line.match(/^\s*-\s+(.*)$/);
            if (m) {
                flushPara();
                items.push(m[1]);
            } else if (line.trim()) {
                flushList();
                para.push(line);
            }
        }
        flushPara();
        flushList();
    }
    return blocks;
}

/**
 * Les FEUILLES de texte d'une ligne, dans l'ordre exact où le rendu les
 * affiche : liens d'abord (le libellé remplace l'URL), puis split RICH_RE
 * (les fragments code perdent leurs backticks), puis emphase (les marqueurs
 * ** et * disparaissent). La concaténation des feuilles est donc le texte
 * TEL QU'IL EST LU À L'ÉCRAN — c'est sur elle que le karaoké compte ses mots.
 */
export function lineDisplayLeaves(line) {
    const leaves = [];
    for (const seg of splitLinks(line)) {
        const inner = seg.type === "link" ? seg.label : seg.text;
        const parts = String(inner == null ? "" : inner).split(RICH_RE);
        for (let i = 0; i < parts.length; i++) {
            const p = parts[i];
            if (!p) continue;
            if (i % 2 === 1) {
                leaves.push(/^`[^`]+`$/.test(p) ? p.slice(1, -1) : p);
                continue;
            }
            for (const run of splitEmphasis(p)) {
                if (run.text) leaves.push(run.text);
            }
        }
    }
    return leaves;
}
